import { useState, useEffect } from 'react'
import { aiConfigApi } from '../services/aiConfig'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  Modal,
  FormField,
  FormSelect,
} from '../components/common'
import { Cpu, RefreshCw, Zap, Brain, Sparkles, Check, X, FlaskConical, Settings, DollarSign } from 'lucide-react'

const tierDefaults = {
  haiku: { modelId: 'us.anthropic.claude-haiku-4-5-20251001-v1:0', inputPrice: 1.0, outputPrice: 5.0 },
  sonnet: { modelId: 'us.anthropic.claude-sonnet-4-6', inputPrice: 3.0, outputPrice: 15.0 },
  opus: { modelId: 'us.anthropic.claude-opus-4-6', inputPrice: 15.0, outputPrice: 75.0 },
}

const AIModels = () => {
  const [configs, setConfigs] = useState([])
  const [availableModels, setAvailableModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editConfig, setEditConfig] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [testingModel, setTestingModel] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [quickSelectLoading, setQuickSelectLoading] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [configsRes, modelsRes] = await Promise.allSettled([
        aiConfigApi.getConfigs(),
        aiConfigApi.getAvailableModels(),
      ])

      if (configsRes.status === 'fulfilled') setConfigs(configsRes.value.data)
      if (modelsRes.status === 'fulfilled') setAvailableModels(modelsRes.value.data)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const openEditModal = (config) => {
    setEditConfig(config)
    setEditForm({
      modelId: config.modelId,
      maxTokens: config.maxTokens,
      temperature: config.temperature,
      isActive: config.isActive,
      inputPricePerMillionTokens: config.inputPricePerMillionTokens,
      outputPricePerMillionTokens: config.outputPricePerMillionTokens,
      costPerPage: config.costPerPage,
    })
  }

  const handleSave = async () => {
    if (!editConfig || !editForm.modelId) return
    setSaving(true)
    try {
      await aiConfigApi.updateConfig(editConfig.serviceType, editForm)
      setEditConfig(null)
      setSuccessMessage(`Updated ${editConfig.serviceTypeName} model`)
      await loadData()
    } catch (err) {
      showError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleTierSelect = (tier) => {
    const defaults = tierDefaults[tier]
    if (!defaults) return
    setEditForm(prev => ({
      ...prev,
      modelId: defaults.modelId,
      inputPricePerMillionTokens: defaults.inputPrice,
      outputPricePerMillionTokens: defaults.outputPrice,
    }))
  }

  const handleQuickSelect = async (tier) => {
    setQuickSelectLoading(tier)
    try {
      await aiConfigApi.quickSelect(tier)
      setSuccessMessage(`All ${tier} models upgraded to latest`)
      await loadData()
    } catch (err) {
      showError(err)
    } finally {
      setQuickSelectLoading(null)
    }
  }

  const handleTest = async (modelId) => {
    setTestingModel(modelId)
    setTestResult(null)
    try {
      const res = await aiConfigApi.testModel(modelId)
      setTestResult(res.data)
    } catch (err) {
      setTestResult({ success: false, error: extractErrorMessage(err) })
    } finally {
      setTestingModel(null)
    }
  }

  const getModelShortName = (modelId) => {
    if (!modelId) return 'Unknown'
    if (modelId.includes('opus')) return 'Opus'
    if (modelId.includes('sonnet')) return 'Sonnet'
    if (modelId.includes('haiku')) return 'Haiku'
    return modelId.split('.').pop()?.split('-')[0] || modelId
  }

  const getModelBadgeVariant = (modelId) => {
    if (!modelId) return 'default'
    if (modelId.includes('opus')) return 'danger'
    if (modelId.includes('sonnet')) return 'primary'
    if (modelId.includes('haiku')) return 'success'
    return 'default'
  }

  const modelOptions = availableModels.map(m => ({
    value: m.modelId,
    label: `${m.modelName} (${m.modelId})`,
  }))

  if (loading) return <LoadingSpinner />
  if (error && !configs.length) return <ErrorMessage message={error} />

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Model Configuration"
        subtitle="Manage which AI models are used for each service"
        actions={
          <Button variant="ghost" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        }
      />

      {successMessage && (
        <div className="bg-accent-50 border border-accent-200 text-accent-700 px-4 py-3 rounded-card flex items-center gap-2">
          <Check size={16} /> {successMessage}
        </div>
      )}

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {/* Quick Select */}
      <Card>
        <CardHeader title="Quick Select" />
        <CardBody>
          <p className="text-sm text-dark-400 mb-4">
            Update all configs within a tier to the latest model version of that tier.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleQuickSelect('haiku')}
              disabled={!!quickSelectLoading}
            >
              {quickSelectLoading === 'haiku' ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Zap size={14} className="mr-2" />}
              Upgrade Haiku
            </Button>
            <Button
              variant="primary"
              onClick={() => handleQuickSelect('sonnet')}
              disabled={!!quickSelectLoading}
            >
              {quickSelectLoading === 'sonnet' ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Brain size={14} className="mr-2" />}
              Upgrade Sonnet
            </Button>
            <Button
              variant="danger"
              onClick={() => handleQuickSelect('opus')}
              disabled={!!quickSelectLoading}
            >
              {quickSelectLoading === 'opus' ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
              Upgrade Opus
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Model Config Cards */}
      <div>
        <h2 className="text-lg font-semibold text-dark-800 mb-4">Service Model Mappings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {configs.map((config) => (
            <Card key={config.id}>
              <CardBody>
                <div className="flex items-start justify-between mb-3">
                  <Badge variant={getModelBadgeVariant(config.modelId)}>
                    {getModelShortName(config.modelId)}
                  </Badge>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${config.isActive ? 'text-accent-600' : 'text-dark-400'}`}>
                    {config.isActive ? <Check size={12} /> : <X size={12} />}
                    {config.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <h3 className="font-semibold text-dark-800 text-sm mb-1">
                  {config.displayName || config.serviceTypeName}
                </h3>
                <code className="text-[10px] bg-dark-100 px-1.5 py-0.5 rounded font-mono text-dark-500 block truncate mb-3">
                  {config.modelId}
                </code>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div className="bg-dark-50 rounded-lg px-2 py-1.5">
                    <span className="text-dark-400 block">Max Tokens</span>
                    <span className="font-semibold text-dark-700">{config.maxTokens?.toLocaleString() || '4,096'}</span>
                  </div>
                  <div className="bg-dark-50 rounded-lg px-2 py-1.5">
                    <span className="text-dark-400 block">Temperature</span>
                    <span className="font-semibold text-dark-700">{config.temperature ?? 0.7}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-1.5 text-xs mb-4">
                  <div className="bg-dark-50 rounded-lg px-2 py-1.5 text-center">
                    <span className="text-dark-400 block text-[10px]">In $/1M</span>
                    <span className="font-semibold text-dark-700">${config.inputPricePerMillionTokens ?? 0}</span>
                  </div>
                  <div className="bg-dark-50 rounded-lg px-2 py-1.5 text-center">
                    <span className="text-dark-400 block text-[10px]">Out $/1M</span>
                    <span className="font-semibold text-dark-700">${config.outputPricePerMillionTokens ?? 0}</span>
                  </div>
                  <div className="bg-dark-50 rounded-lg px-2 py-1.5 text-center">
                    <span className="text-dark-400 block text-[10px]">$/Page</span>
                    <span className="font-semibold text-dark-700">${config.costPerPage ?? 0}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleTest(config.modelId)}
                    disabled={testingModel === config.modelId}
                  >
                    {testingModel === config.modelId ? (
                      <RefreshCw size={14} className="animate-spin mr-1" />
                    ) : (
                      <FlaskConical size={14} className="mr-1" />
                    )}
                    Test
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(config)}
                  >
                    <Settings size={14} className="mr-1" /> Edit
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <Card>
          <CardHeader title="Model Test Result" />
          <CardBody>
            <div className="flex items-center gap-4">
              <Badge variant={testResult.success ? 'success' : 'danger'}>
                {testResult.success ? 'Success' : 'Failed'}
              </Badge>
              {testResult.latencyMs && (
                <span className="text-sm text-dark-400">{testResult.latencyMs}ms</span>
              )}
              {testResult.inputTokens != null && (
                <span className="text-sm text-dark-400">In: {testResult.inputTokens} / Out: {testResult.outputTokens}</span>
              )}
            </div>
            {testResult.response && (
              <p className="mt-2 text-sm text-dark-600">Response: {testResult.response}</p>
            )}
            {testResult.error && (
              <p className="mt-2 text-sm text-red-600">{testResult.error}</p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Available Models */}
      {availableModels.length > 0 && (
        <Card>
          <CardHeader title={`Available Models (${availableModels.length})`} />
          <CardBody padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-700/10">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Model ID</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Provider</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Input</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Output</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-dark-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-700/5">
                  {availableModels.map((model) => (
                    <tr key={model.modelId} className="hover:bg-dark-50/50 transition-colors">
                      <td className="px-6 py-3">
                        <code className="text-xs font-mono">{model.modelId}</code>
                      </td>
                      <td className="px-6 py-3 text-sm">{model.modelName}</td>
                      <td className="px-6 py-3 text-sm text-dark-500">{model.provider || '—'}</td>
                      <td className="px-6 py-3 text-xs text-dark-500">{model.inputModalities || '—'}</td>
                      <td className="px-6 py-3 text-xs text-dark-500">{model.outputModalities || '—'}</td>
                      <td className="px-6 py-3">
                        <Badge variant={model.isActive ? 'success' : 'default'}>
                          {model.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTest(model.modelId)}
                          disabled={!!testingModel}
                        >
                          <FlaskConical size={14} className="mr-1" /> Test
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Edit Modal */}
      {editConfig && (
        <Modal
          isOpen={true}
          onClose={() => setEditConfig(null)}
          title={`Edit Model — ${editConfig.displayName || editConfig.serviceTypeName}`}
        >
          <div className="space-y-4">
            {/* Tier Quick Select Buttons */}
            <div>
              <label className="block text-xs font-medium text-dark-500 mb-2">Quick Select Tier</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleTierSelect('haiku')}
                  className="flex-1 px-3 py-2 text-xs font-medium rounded-button border border-dark-200 hover:bg-accent-50 hover:border-accent-300 transition-colors"
                >
                  <Zap size={12} className="inline mr-1" /> Haiku
                </button>
                <button
                  type="button"
                  onClick={() => handleTierSelect('sonnet')}
                  className="flex-1 px-3 py-2 text-xs font-medium rounded-button border border-dark-200 hover:bg-primary-50 hover:border-primary-300 transition-colors"
                >
                  <Brain size={12} className="inline mr-1" /> Sonnet
                </button>
                <button
                  type="button"
                  onClick={() => handleTierSelect('opus')}
                  className="flex-1 px-3 py-2 text-xs font-medium rounded-button border border-dark-200 hover:bg-red-50 hover:border-red-300 transition-colors"
                >
                  <Sparkles size={12} className="inline mr-1" /> Opus
                </button>
              </div>
            </div>

            {/* Model Selection */}
            {modelOptions.length > 0 ? (
              <FormSelect
                label="Model"
                value={editForm.modelId}
                onChange={(e) => setEditForm(prev => ({ ...prev, modelId: e.target.value }))}
                options={modelOptions}
              />
            ) : (
              <FormField
                label="Model ID"
                value={editForm.modelId}
                onChange={(e) => setEditForm(prev => ({ ...prev, modelId: e.target.value }))}
                placeholder="us.anthropic.claude-sonnet-4-6"
              />
            )}

            {/* Max Tokens & Temperature */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label="Max Tokens"
                type="number"
                value={editForm.maxTokens}
                onChange={(e) => setEditForm(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 0 }))}
              />
              <FormField
                label="Temperature"
                type="number"
                value={editForm.temperature}
                onChange={(e) => setEditForm(prev => ({ ...prev, temperature: parseFloat(e.target.value) || 0 }))}
                step="0.1"
                min="0"
                max="1"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-dark-600">Active</label>
              <button
                type="button"
                onClick={() => setEditForm(prev => ({ ...prev, isActive: !prev.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editForm.isActive ? 'bg-accent-500' : 'bg-dark-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    editForm.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Pricing */}
            <div>
              <label className="block text-xs font-medium text-dark-500 mb-2">
                <DollarSign size={12} className="inline mr-1" />Pricing (per 1M tokens)
              </label>
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  label="Input Price"
                  type="number"
                  value={editForm.inputPricePerMillionTokens}
                  onChange={(e) => setEditForm(prev => ({ ...prev, inputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  min="0"
                />
                <FormField
                  label="Output Price"
                  type="number"
                  value={editForm.outputPricePerMillionTokens}
                  onChange={(e) => setEditForm(prev => ({ ...prev, outputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  min="0"
                />
                <FormField
                  label="Cost/Page"
                  type="number"
                  value={editForm.costPerPage}
                  onChange={(e) => setEditForm(prev => ({ ...prev, costPerPage: parseFloat(e.target.value) || 0 }))}
                  step="0.001"
                  min="0"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
              <Button variant="ghost" onClick={() => setEditConfig(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={saving || !editForm.modelId}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default AIModels
